import { Button, Collapse, Divider, Grid, Paper, Tab, Tabs, Typography } from "@mui/material";
import objectHash from "object-hash";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import CloseIconButton from "../../../../../../shared/components/CloseIconButton";
import HorizontalFill from "../../../../../../shared/components/HorizontalFill";
import { DimensionDescriptor } from "../../../../../../shared/reporting/api/biClient.types";
import cloneDeep from "../../../../../../shared/utilities/cloneDeep";
import { FieldWithOrder, FieldWithSorting } from "../../../../../hooks/FieldWithOrder";
import { useLocalization } from "../../../../../hooks/useLocalization";
import ColumnsContainer from "./ColumnsContainer";
import { OptionField } from "./ColumnsOptions.types";
import SortingContainer from "./sorting/SortingContainer";
import { DimensionsStructure } from "../../../../../../shared/reporting/types/biShared.types.ts";

const isSelectionValid = (selection: FieldWithOrder[]) => {
  return !selection.some((fwo) => !fwo.field.caption);
};

enum TabNames {
  "columns" = "columns",
  "sorting" = "sorting",
}

interface Props {
  open: boolean;
  selectedFields: FieldWithOrder[];
  sortedFields: FieldWithSorting[];
  dimensions: DimensionDescriptor[];
  groupStructure: DimensionsStructure;
  withAllocation: boolean;
  onApplySelection: (selection: FieldWithOrder[]) => void;
  onApplySorting: (sorting: FieldWithSorting[]) => void;
  onClose: () => void;
}

export default function ColumnsOptions(props: Props) {
  const {
    dimensions,
    selectedFields,
    sortedFields,
    open,
    groupStructure,
    withAllocation,
    onApplySelection,
    onApplySorting,
    onClose,
  } = props;

  const { drilldown: locale } = useLocalization();

  const [selectedTab, setSelectedTab] = useState(TabNames.columns);
  const [dirtyColumnsSelection, setDirtyColumnsSelection] = useState<FieldWithOrder[]>(() => cloneDeep(selectedFields));
  const [dirtyColumnsSorting, setDirtyColumnsSorting] = useState<FieldWithSorting[]>(() => cloneDeep(sortedFields));

  const validDimensions = React.useMemo(
    () => (withAllocation ? dimensions : dimensions.filter((d) => d.glAllowed === true)),
    [withAllocation, dimensions]
  );

  const updateDirtyColumnsSelection = useCallback((selection: FieldWithOrder[]) => {
    setDirtyColumnsSelection(cloneDeep(selection));
    setDirtyColumnsSorting((prev) =>
      cloneDeep(prev.filter((sf) => selection.some((f) => f.field.name === sf.field.name)))
    );
  }, []);

  useEffect(() => {
    updateDirtyColumnsSelection(selectedFields);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFields]);

  useEffect(() => setDirtyColumnsSorting(cloneDeep(sortedFields)), [sortedFields]);

  const columnsHasChanged = useMemo(() => {
    const originalFieldsHash = objectHash(selectedFields);
    const dirtySelectionHash = objectHash(dirtyColumnsSelection);
    if (originalFieldsHash === dirtySelectionHash) {
      return false;
    }
    return isSelectionValid(dirtyColumnsSelection);
  }, [selectedFields, dirtyColumnsSelection]);

  const sortingHasChanged = useMemo(() => {
    const originalFieldsHash = objectHash(sortedFields);
    const dirtySelectionHash = objectHash(dirtyColumnsSorting);
    if (originalFieldsHash === dirtySelectionHash) {
      return false;
    }
    return isSelectionValid(dirtyColumnsSorting);
  }, [sortedFields, dirtyColumnsSorting]);

  const options = useMemo(() => {
    const getGroupName = (field: DimensionDescriptor) => {
      const group = groupStructure.groups.find((g) => g.dimensions.indexOf(field.name) > -1);
      return group?.caption || "General";
    };
    return validDimensions
      .map((f) => ({ id: f.name, label: f.caption, field: f, group: getGroupName(f) }) as OptionField)
      .sort((a, b) => {
        if (a.group > b.group) return 1;
        if (a.group < b.group) return -1;
        return 0;
      });
  }, [validDimensions, groupStructure]);

  const availableSortingOptions = useMemo(() => {
    return options.filter((op) => dirtyColumnsSelection.some((sf) => sf.field.name === op.id));
  }, [options, dirtyColumnsSelection]);

  const applySelection = useCallback(() => {
    if (columnsHasChanged) {
      dirtyColumnsSelection.forEach((field, index) => {
        field.order = index;
      });
      onApplySelection(cloneDeep(dirtyColumnsSelection));
    }
    if (sortingHasChanged) {
      dirtyColumnsSorting.forEach((field, index) => {
        field.order = index;
      });
      onApplySorting(cloneDeep(dirtyColumnsSorting));
    }
    onClose();
  }, [
    dirtyColumnsSelection,
    dirtyColumnsSorting,
    columnsHasChanged,
    sortingHasChanged,
    onApplySelection,
    onApplySorting,
    onClose,
  ]);

  const closeDialog = useCallback(() => {
    updateDirtyColumnsSelection(cloneDeep(selectedFields));
    onClose();
  }, [selectedFields, onClose, updateDirtyColumnsSelection]);

  return (
    <Collapse
      in={open}
      orientation="horizontal"
      sx={{
        position: "absolute",
        top: 0,
        right: 0,
        height: "100%",
        borderRadius: 0,
        zIndex: 500,
      }}
    >
      <Paper
        sx={{
          display: "flex",
          flex: 1,
          width: "348px",
          height: "100%",
          borderRadius: 0,
        }}
        elevation={3}
      >
        <Grid container sx={{ flexDirection: "column", width: "100%" }}>
          <Grid container sx={{ px: 3, pt: 2, alignItems: "center", width: "100%" }}>
            <Typography variant="h6">{locale.column_options_label}</Typography>
            <HorizontalFill />
            <CloseIconButton onClick={closeDialog} />
          </Grid>
          <Grid container width={"100%"}>
            <Tabs
              sx={{ display: "flex", flex: 1 }}
              variant="fullWidth"
              value={selectedTab}
              onChange={(_, value: TabNames) => setSelectedTab(value)}
            >
              <Tab label="Columns" value={TabNames.columns} />
              <Tab label="Sorting" value={TabNames.sorting} />
            </Tabs>
          </Grid>
          <Divider />
          <Grid container sx={{ flex: 1, overflowY: "auto", alignItems: "start", width: "100%" }}>
            <DndProvider backend={HTML5Backend}>
              {selectedTab === TabNames.columns && (
                <ColumnsContainer
                  selectedFields={dirtyColumnsSelection}
                  options={options}
                  onSelectionChanged={updateDirtyColumnsSelection}
                />
              )}
              {selectedTab === TabNames.sorting && (
                <SortingContainer
                  selectedFields={dirtyColumnsSorting}
                  options={availableSortingOptions}
                  onSelectionChanged={setDirtyColumnsSorting}
                />
              )}
            </DndProvider>
          </Grid>

          <Divider />
          <Grid container sx={{ gap: 1, px: 2, py: 2, width: "100%" }}>
            <HorizontalFill />
            <Button variant="text" color="secondary" onClick={closeDialog}>
              Cancel
            </Button>
            <Button variant="contained" disabled={!columnsHasChanged && !sortingHasChanged} onClick={applySelection}>
              Apply
            </Button>
          </Grid>
        </Grid>
      </Paper>
    </Collapse>
  );
}
